import { useState } from 'react';
import { Signal, SignalHigh, SignalMedium, SignalLow, SignalZero, AlertTriangle } from 'lucide-react';
import { useSoftphone } from '@/store/softphone';
import clsx from 'clsx';

/**
 * Tek bakışta ses kalitesi göstergesi. 0-5 bar (MOS bazlı).
 * Tıklayınca detay popover: codec, loss%, jitter, RTT.
 */
export function SignalBars({ compact = false }: { compact?: boolean }) {
  const q = useSoftphone((s) => s.quality);
  const [open, setOpen] = useState(false);

  if (!q) return null;

  const cls = barColor(q.bars);
  const Icon = barIcon(q.bars);
  const label = barLabel(q.bars, q.mos);

  return (
    <div className="relative inline-flex">
      <button
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset transition',
          cls,
          compact && 'px-1.5',
        )}
        title="Ses kalitesi detayları"
      >
        <Icon size={11} />
        {!compact && <span>{label}</span>}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 top-full mt-1.5 right-0 w-60 bg-surface rounded-xl shadow-xl ring-1 ring-black/5 p-3 text-[11px]">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-slate-800 text-[12px]">Ses Kalitesi</span>
              <BarVisual bars={q.bars} />
            </div>
            <dl className="space-y-1">
              <Row label="MOS" value={q.mos != null ? q.mos.toFixed(2) : '—'} hint="3.5+ iyi · 4.0+ HD" />
              <Row label="Kayıp" value={`${q.lossPct.toFixed(1)}%`} alert={q.lossPct > 3} />
              <Row label="Jitter" value={`${q.jitterMs.toFixed(0)} ms`} alert={q.jitterMs > 30} />
              <Row label="RTT" value={`${q.rttMs.toFixed(0)} ms`} alert={q.rttMs > 200} />
              <Row label="Codec" value={q.codecIn} mono />
            </dl>
            {q.bars <= 2 && q.mos != null && (
              <div className="mt-2 flex items-start gap-1.5 px-2 py-1.5 bg-amber-50 rounded-lg text-amber-800 text-[10px]">
                <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
                <span>
                  {q.lossPct > 5 ? 'Yüksek paket kaybı — ağ bağlantını kontrol et.' :
                    q.jitterMs > 50 ? 'Jitter yüksek — wifi yerine ethernet dene.' :
                      q.rttMs > 250 ? 'Yüksek gecikme — uzak sunucu / yoğun trafik.' :
                        'Bağlantı kalitesi düşük.'}
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value, hint, alert, mono }: {
  label: string; value: string; hint?: string; alert?: boolean; mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-500">
        {label}
        {hint && <span className="text-slate-400 text-[10px] ml-1">· {hint}</span>}
      </dt>
      <dd className={clsx(mono && 'font-mono', alert ? 'text-amber-700 font-semibold' : 'text-slate-800')}>
        {value}
      </dd>
    </div>
  );
}

function BarVisual({ bars }: { bars: number }) {
  return (
    <div className="flex items-end gap-0.5 h-3.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={clsx(
            'w-1 rounded-sm',
            i <= bars ? barFill(bars) : 'bg-slate-200',
          )}
          style={{ height: `${30 + i * 14}%` }}
        />
      ))}
    </div>
  );
}

function barColor(bars: number): string {
  if (bars >= 4) return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  if (bars >= 3) return 'bg-blue-50 text-blue-700 ring-blue-200';
  if (bars >= 2) return 'bg-amber-50 text-amber-700 ring-amber-200';
  return 'bg-red-50 text-red-700 ring-red-200';
}

function barFill(bars: number): string {
  if (bars >= 4) return 'bg-emerald-500';
  if (bars >= 3) return 'bg-blue-500';
  if (bars >= 2) return 'bg-amber-500';
  return 'bg-red-500';
}

function barIcon(bars: number): React.ComponentType<any> {
  if (bars >= 4) return SignalHigh;
  if (bars >= 3) return Signal;
  if (bars >= 2) return SignalMedium;
  if (bars >= 1) return SignalLow;
  return SignalZero;
}

function barLabel(bars: number, mos: number | null): string {
  if (mos == null) return 'Ölçülüyor…';
  if (bars >= 5) return 'Mükemmel';
  if (bars >= 4) return 'İyi';
  if (bars >= 3) return 'Orta';
  if (bars >= 2) return 'Zayıf';
  return 'Kötü';
}
